import * as React from 'react';
import { useSelector } from 'react-redux';
import { selectAdvertisingApiCsrfToken } from 'src/app/slice';
import { useInternalChromeConfigContext } from 'src/providers/InternalChromeConfigContextProvider';
import {
  advertisingApiClient,
  AdvertisingApiClientProxy,
} from 'src/commons/utils/advertising-api-client';
import contextHookCreator from 'src/commons/utils/context-hook-creator';
import { CONSOLE_NOTIFICATIONS_API_CLIENT_ID } from 'src/commons/constants';

type AdvertisingApiClientContextProps = {
  advertisingApiClient: AdvertisingApiClientProxy;
  advertisingApiCsrfToken: string;
};

const { hostname, protocol } = window.location;
const apiEndpointPath = `${protocol}//${hostname}/a9g-api-gateway`;

// converted from `AdvertisingExperienceChromeNavigation` in http://advertising-api-sleuth-na.aka.amazon.com/clientId
const apiClientId = CONSOLE_NOTIFICATIONS_API_CLIENT_ID;

const AdvertisingApiClientContext =
  React.createContext<AdvertisingApiClientContextProps>({
    advertisingApiClient,
    advertisingApiCsrfToken: '',
  });

export const useAdvertisingApiContext =
  contextHookCreator<AdvertisingApiClientContextProps>(
    AdvertisingApiClientContext,
    'useAdvertisingApiContext',
    'AdvertisingApiClientProvider',
  );

export const AdvertisingApiClientProvider: React.FC = ({ children }) => {
  const internalContext = useInternalChromeConfigContext();
  // Need to pass CSRF token to conditionally render the bell drawer
  // only when the CSRF token is available
  const advertisingApiCsrfToken = useSelector(selectAdvertisingApiCsrfToken);
  advertisingApiClient.init({
    clientId: apiClientId,
    csrfToken: advertisingApiCsrfToken,
    // relative path to the domain. see https://tiny.amazon.com/19zujy9kg/codeamazpackAdveblobmainconf
    // maybe this should be passed from the server?
    endpoint: apiEndpointPath,
    entityId: internalContext.config.entityId,
    isImpersonator: internalContext.config.isSpoofer || false, // need to figure out how to determine this
    marketplaceId: internalContext.config.marketplaceId,
  });

  return (
    <AdvertisingApiClientContext.Provider
      value={{ advertisingApiClient, advertisingApiCsrfToken }}
    >
      {children}
    </AdvertisingApiClientContext.Provider>
  );
};
